import { useEffect, useState } from 'react'
import type { User } from 'firebase/auth'
import { Menu } from 'lucide-react'
import type { Language, Translation } from '../../translations'
import { useChatFirestore } from './hooks/useChatFirestore'
import { useChatSuggestions } from './hooks/useChatSuggestions'
import { ChatMessageList } from './components/ChatMessageList'
import { ChatComposer } from './components/ChatComposer'
import { ChatSidebar } from './components/ChatSidebar'
import { ChatToast } from './components/ChatToast'
import { CHAT_URL } from './constants'
import type { Message } from './types'

interface ChatPageProps {
    lang: Language
    t: Translation
    user: User | null
    onLogin: () => Promise<void>
    onShowUpgrade?: () => void
}

export function ChatPage({
    lang,
    t,
    user,
    onLogin,
    onShowUpgrade
}: ChatPageProps) {
    const {
        conversations,
        activeId,
        messages,
        startNewChat,
        selectConversation,
        addMessage,
        deleteConversation,
        renameConversation
    } = useChatFirestore({ user, lang })

    const [input, setInput] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [toast, setToast] = useState<string | null>(null)
    const [expandedSources, setExpandedSources] = useState<Record<string, boolean>>({})
    const [isSidebarOpen, setIsSidebarOpen] = useState(false)

    const {
        chatSuggestions,
        isSuggestOpen,
        suggestIndex,
        handleInputChange,
        handleInputFocus,
        handleInputBlur,
        handleInputKeyDown,
        acceptChatSuggestion,
        inputRef
    } = useChatSuggestions({ input, setInput, lang, messages })

    useEffect(() => {
        if (!toast) return
        const timer = window.setTimeout(() => setToast(null), 1500)
        return () => window.clearTimeout(timer)
    }, [toast])

    const copyToClipboard = async (value: string) => {
        try {
            await navigator.clipboard.writeText(value)
            setToast('Copied')
        } catch (error) {
            console.error('Clipboard error', error)
            setToast('Copy failed')
        }
    }

    const downloadText = (filename: string, content: string) => {
        try {
            const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
            const url = URL.createObjectURL(blob)
            const anchor = document.createElement('a')
            anchor.href = url
            anchor.download = filename
            anchor.click()
            URL.revokeObjectURL(url)
        } catch (error) {
            console.error('Download error', error)
        }
    }

    const toggleSourceExpanded = (key: string) => {
        setExpandedSources((prev) => ({ ...prev, [key]: !prev[key] }))
    }

    const handleRelatedQuestionClick = (question: string) => {
        setInput(question)
        inputRef.current?.focus()
    }

    const handleClearChat = () => {
        if (isLoading) return
        startNewChat()
        setExpandedSources({})
    }

    const sendMessage = async (override?: string) => {
        const textToSend = (override ?? input).trim()
        if (!textToSend || isLoading) return

        if (!user) {
            onLogin()
            return
        }

        const userMessage = textToSend
        if (!override) setInput('')
        setIsLoading(true)

        // Save user message immediately to Firestore and get/update conversation ID
        const currentConvId = await addMessage({ role: 'user', content: userMessage })

        try {
            const token = await user.getIdToken()
            const response = await fetch(CHAT_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    message: userMessage,
                    lang,
                    // Backend pulls the latest message from 'messages' if present.
                    // So we must include the current user message at the end.
                    messages: [
                        ...messages.map(m => ({ role: m.role, content: m.content })),
                        { role: 'user', content: userMessage }
                    ].slice(-6)
                })
            })

            if (response.status === 402) {
                onShowUpgrade?.()
                setIsLoading(false)
                return
            }

            if (!response.ok) throw new Error('Failed to chat')

            const data = await response.json()
            const aiMessage: Message = {
                role: 'model',
                content: data.answer,
                context_used: data.context,
                citations: data.citations,
                results: data.results,
                related_questions: data.related_questions
            }

            // Save AI response to the SAME conversation
            if (currentConvId) {
                await addMessage(aiMessage, currentConvId)
            }
        } catch (error) {
            console.error('Chat error', error)
            await addMessage({ role: 'model', content: t.chatError })
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="flex h-full overflow-hidden bg-slate-50">
            {/* Sidebar Toggle for Mobile */}
            <button
                onClick={() => setIsSidebarOpen(true)}
                className="fixed left-4 top-4 z-30 rounded-lg bg-white p-2 shadow-sm border border-slate-200 lg:hidden"
            >
                <Menu className="h-5 w-5 text-slate-600" />
            </button>

            {/* Sidebar */}
            <ChatSidebar
                conversations={conversations}
                activeId={activeId}
                onSelect={selectConversation}
                onNewChat={startNewChat}
                onDelete={deleteConversation}
                onRename={(id) => {
                    const newTitle = prompt(t.chatRename)
                    if (newTitle) renameConversation(id, newTitle)
                }}
                t={t}
                isOpen={isSidebarOpen}
                onClose={() => setIsSidebarOpen(false)}
            />

            {/* Mobile Sidebar Overlay */}
            {isSidebarOpen && (
                <div
                    className="fixed inset-0 z-30 bg-slate-900/20 backdrop-blur-sm lg:hidden transition-opacity"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            {/* Main Chat Area */}
            <main className="relative flex flex-1 flex-col overflow-hidden bg-white">
                <ChatToast message={toast} />

                <div className="custom-scrollbar flex-1 overflow-y-auto pb-32">
                    <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
                        <ChatMessageList
                            messages={messages.length > 0 ? messages : [{ role: 'model', content: t.chatGreeting }]}
                            t={t}
                            onCopyAnswer={copyToClipboard}
                            onDownloadAnswer={downloadText}
                            onCopyLink={copyToClipboard}
                            expandedSources={expandedSources}
                            onToggleSource={toggleSourceExpanded}
                            onRelatedQuestionClick={handleRelatedQuestionClick}
                        />

                        {isLoading && (
                            <div className="mt-4 flex justify-start">
                                <div className="rounded-2xl bg-slate-50 p-4 text-sm italic text-slate-500 animate-pulse">
                                    {t.chatThinking}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-white via-white to-transparent p-4 sm:p-6 pt-12">
                    <ChatComposer
                        input={input}
                        onInputChange={handleInputChange}
                        onInputFocus={handleInputFocus}
                        onInputBlur={handleInputBlur}
                        onInputKeyDown={(event) => handleInputKeyDown(event, sendMessage)}
                        inputRef={inputRef}
                        chatSuggestions={chatSuggestions}
                        isSuggestOpen={isSuggestOpen}
                        suggestIndex={suggestIndex}
                        onAcceptSuggestion={acceptChatSuggestion}
                        onSend={() => sendMessage()}
                        onClearChat={handleClearChat}
                        isLoading={isLoading}
                        placeholder={t.chatPlaceholder}
                        t={t}
                        user={user}
                        onLogin={onLogin}
                    />
                </div>
            </main>
        </div>
    )
}
